import * as classNames from 'classnames';
import { UserType } from 'common/interfaces/user';
import { Checkbox, IconButton } from 'components/action';
import { SavingIndicator, SavingIndicatorStatus } from 'components/indicator';
import { DropdownContainer, OptionsList } from 'components/list';
import { scrollWindow } from 'events/scroll';
import toString from 'lodash-es/toString';
import { action, computed } from 'mobx';
import { inject, observer } from 'mobx-react';
import * as React from 'react';
import { IAppOnlyStore } from 'stores/AllStores';
import { LeadEditableProperties } from 'stores/Leads';
import { browserHistory } from './../../../../App';
import {
  ISalesRep,
  LeadGroupModalStatus,
  IProviderShortWithSlpcode,
  IProviderTypeOrClass,
} from 'common/interfaces/provider';

// Styles
const s = require('./LeadDetailsMaster.css');
const companyIcon = require('assets/document profile icon.svg');
const businessTypeIcon = require('assets/briefcase icon.svg');
const licenseTypeIcon = require('assets/license icon.svg');
const addressIcon = require('assets/building icon.svg');
const contactIcon = require('assets/conversation icon.svg');
const groupIcon = require('assets/documents icon.svg');
const arrowIcon = require('assets/blue arrow.svg');
const repIcon = require('assets/contact icon.svg');
const primaryIcon = require('assets/primary icon.svg');
const idIcon = require('assets/id icon.svg');
const deleteIcon = require('assets/close orange icon.svg');

// Component
@inject('store')
@observer
export class LeadDetailsMaster extends React.Component<IAppOnlyStore, {}> {
  constructor(props: IAppOnlyStore) {
    super(props);

    this.props.store.lead.fetchBusinessTypes();
    this.props.store.lead.fetchDrugLicenseTypes();
  }

  @computed
  private get otherLeadsFromSameBusinessGroup(): IProviderShortWithSlpcode[] {
    const { lead, leadGroup } = this.props.store;

    return !leadGroup.loading
      ? leadGroup.openedGroup.provider.filter(
          (provider) => provider.databaseId !== lead.openLeadId
        )
      : [];
  }

  @computed private get businessGroupName(): string {
    const { openLeadDetails } = this.props.store.lead;

    return openLeadDetails !== null &&
      openLeadDetails.providerGroup !== null &&
      typeof openLeadDetails.providerGroup.name !== 'undefined' &&
      openLeadDetails.providerGroup.name !== null
      ? openLeadDetails.providerGroup.name
      : '';
  }

  @computed private get leadSalesReps(): ISalesRep[] {
    const { lead } = this.props.store;

    return lead.openLeadEditingDetails && lead.editedOpenLeadDetails !== null
      ? lead.editedOpenLeadDetails.salesReps
      : lead.openLeadDetails.salesReps;
  }

  @computed private get leadSalesRepsIds(): number[] {
    return this.leadSalesReps.map((rep) => rep.id);
  }

  @computed private get sortedLeadSalesReps(): ISalesRep[] {
    return this.leadSalesReps.length > 0
      ? this.leadSalesReps.sort((repA, repB) => {
          return repA.primary && !repB.primary
            ? -1
            : !repA.primary && repB.primary
            ? 1
            : 0;
        })
      : [];
  }

  @computed private get commaSeparatedStringOfLeadsSalesReps(): string {
    return this.sortedLeadSalesReps
      .map((rep): string => rep.name || rep.username)
      .join(', ');
  }

  @computed private get salesRepsOptions(): Array<{
    value: number;
    label: string;
  }> {
    const { users } = this.props.store;

    return users.allActiveSalesUsers.length > 0
      ? users.allActiveSalesUsers
          .filter((rep) => {
            if (rep.slpcode === null) {
              return false;
            }

            const foundIndex = this.leadSalesRepsIds.indexOf(rep.id);
            if (foundIndex !== -1) {
              return false;
            }

            return true;
          })
          .map((user) => ({
            value: user.id,
            label: user.name,
          }))
      : [];
  }

  @computed private get providerTypesOptions(): Array<{
    value: string;
    label: string;
  }> {
    const {
      leads: { providerTypes },
    } = this.props.store;

    return providerTypes.list.map((type) => ({
      value: type.code,
      label: type.name,
    }));
  }

  @computed private get dispenserClassesOptions(): Array<{
    value: string;
    label: string;
  }> {
    const {
      leads: { providerDispenserClasses },
    } = this.props.store;

    return providerDispenserClasses.list.map((dispenserClass) => ({
      value: dispenserClass.code,
      label: dispenserClass.name,
    }));
  }

  @computed private get drugLicenseTypeOptions(): Array<{
    value: number;
    label: string;
  }> {
    const { lead } = this.props.store;

    return lead.drugLicenseTypesLength
      ? lead.drugLicenseTypes.map((type) => ({
          value: type.id,
          label: type.name,
        }))
      : [];
  }

  @computed private get openLeadProviderType(): IProviderTypeOrClass | null {
    const {
      leads: { providerTypes },
      lead: { openLeadDetails },
    } = this.props.store;

    if (
      openLeadDetails === null ||
      (openLeadDetails && openLeadDetails.type === null)
    ) {
      return null;
    }

    let index = -1;
    providerTypes.list.some((type, typeIndex) => {
      if (openLeadDetails.type === type.code) {
        index = typeIndex;
        return true;
      }

      return false;
    });

    return index !== -1 ? providerTypes.list[index] : null;
  }

  @computed
  private get openLeadDispenserClass(): IProviderTypeOrClass | null {
    const {
      leads: { providerDispenserClasses },
      lead: { openLeadDetails },
    } = this.props.store;

    if (
      openLeadDetails === null ||
      (openLeadDetails && openLeadDetails.dispenserClass === null)
    ) {
      return null;
    }

    let index = -1;
    providerDispenserClasses.list.some(
      (dispenserClass, dispenserClassIndex) => {
        if (openLeadDetails.dispenserClass === dispenserClass.code) {
          index = dispenserClassIndex;
          return true;
        }

        return false;
      }
    );

    return index !== -1 ? providerDispenserClasses.list[index] : null;
  }

  // Select lead
  @action public handleLeadSelect = (id: number) => (
    e: React.MouseEvent<HTMLButtonElement>
  ) => {
    browserHistory.push('/sales/lead/' + id);
  };

  public render() {
    const {
      openLeadId,
      openLeadAddress,
      openLeadDetails,
      editedOpenLeadDetails,
      openLeadEditingDetails,
      openLeadEditingDetailsSaving,
      openLeadEditingDetailsSavingError,
      drugLicenseTypesLength,
    } = this.props.store.lead;
    const { leadGroup } = this.props.store;
    const { user } = this.props.store;

    return openLeadId !== null ? (
      <div className={classNames({ [s.editing]: openLeadEditingDetails })}>
        {openLeadDetails !== null &&
          (openLeadDetails.dba ||
            openLeadDetails.legalBusName ||
            openLeadDetails.cardcode ||
            openLeadEditingDetails) && (
            <div
              title={
                openLeadDetails.dba || openLeadDetails.legalBusName
                  ? 'Name'
                  : undefined
              }
              className={s.group}
            >
              <img className={s.icon} src={companyIcon} />

              {openLeadDetails.createdManually && openLeadEditingDetails && (
                <div>
                  <h5>Name:</h5>
                  <label className={s.editSection}>
                    <p>
                      <span>Provider ID</span>
                    </p>
                    <input
                      type="text"
                      value={editedOpenLeadDetails.providerId}
                      onChange={this.handleOpenLeadDetailsChange(
                        LeadEditableProperties.providerId
                      )}
                    />
                  </label>
                </div>
              )}

              {(openLeadDetails.dba && openLeadEditingDetails === false && (
                <h1 className={s.companyTitle}>
                  {openLeadDetails.dba || 'Company name'}
                </h1>
              )) ||
                (openLeadEditingDetails && (
                  <label className={s.editSection}>
                    <p>
                      <span>DBA</span>
                    </p>
                    <input
                      type="text"
                      value={editedOpenLeadDetails.dba}
                      onChange={this.handleOpenLeadDetailsChange(
                        LeadEditableProperties.dba
                      )}
                    />
                  </label>
                ))}

              {(openLeadDetails.legalBusName &&
                openLeadEditingDetails === false && (
                  <h3 className={s.companyLegalName}>
                    {openLeadDetails.legalBusName || 'Legal name'}
                  </h3>
                )) ||
                (openLeadEditingDetails && (
                  <label className={s.editSection}>
                    <p>
                      <span>Legal business name</span>
                    </p>
                    <input
                      type="text"
                      value={editedOpenLeadDetails.legalBusName}
                      onChange={this.handleOpenLeadDetailsChange(
                        LeadEditableProperties.legalBusName
                      )}
                    />
                  </label>
                ))}

              {openLeadDetails.cardcode !== null &&
                toString(openLeadDetails.cardcode) !== '0' &&
                openLeadEditingDetails === false && (
                  <h4 className={s.cardcode}>
                    <span>Card code:</span>
                    <strong>{openLeadDetails.cardcode}</strong>
                  </h4>
                )}
            </div>
          )}

        {openLeadDetails !== null &&
          ((openLeadEditingDetails === false &&
            openLeadDetails.providerGroup &&
            openLeadDetails.providerGroup.id) ||
            openLeadEditingDetails === true) && (
            <div
              className={classNames(s.group, s.leadGroup, {
                [s.show]: leadGroup.showList,
                [s.edit]: openLeadEditingDetails === true,
              })}
            >
              {openLeadEditingDetails === false &&
                openLeadDetails.providerGroup &&
                openLeadDetails.providerGroup.id && (
                  <div>
                    <h4 title="Group">
                      Group:{' '}
                      <strong>{openLeadDetails.providerGroup.name}</strong>
                    </h4>
                    <img
                      className={s.icon}
                      src={groupIcon}
                      width="16"
                      height="16"
                    />

                    {leadGroup.openedGroup !== null &&
                      typeof leadGroup.openedGroup.provider !== 'undefined' &&
                      leadGroup.openedGroup.provider.length > 1 && (
                        <IconButton
                          onClick={this.loadLeadGroups}
                          icon={arrowIcon}
                          showLabel={false}
                          iconClassName={classNames(s.arrow, {
                            [s.show]: leadGroup.showList,
                          })}
                        />
                      )}

                    {leadGroup.openedGroup !== null &&
                      typeof leadGroup.openedGroup.provider !== 'undefined' &&
                      leadGroup.openedGroup.provider.length > 1 && (
                        <ul
                          className={classNames(s.leadGroupList, {
                            [s.show]: leadGroup.showList,
                          })}
                        >
                          {this.otherLeadsFromSameBusinessGroup.map(
                            (lead, leadIndex) => (
                              <li key={lead.databaseId}>
                                {((user.userType === UserType.SalesManager ||
                                  user.slpcode === lead.slpcode) && (
                                  <button
                                    className={classNames(
                                      s.providerInGroup,
                                      s.providerInGroupButton
                                    )}
                                    onClick={this.handleLeadSelect(
                                      lead.databaseId
                                    )}
                                  >
                                    <strong>{lead.dba}</strong>
                                    <span>{lead.legalBusName}</span>
                                    <b>Go to lead</b>
                                  </button>
                                )) || (
                                  <p className={s.providerInGroup}>
                                    <strong>{lead.dba}</strong>
                                    <span>{lead.legalBusName}</span>
                                    <em>Assigned to other sales rep</em>
                                  </p>
                                )}
                                <p className={s.providerInGroupCounter}>
                                  #{leadIndex + 1}
                                </p>
                              </li>
                            )
                          )}
                        </ul>
                      )}
                  </div>
                )}

              {openLeadEditingDetails === true && (
                <div>
                  <h5>Business group</h5>

                  <DropdownContainer
                    ref={this.referenceLeadGroupDropdown}
                    value={this.businessGroupName || 'No group'}
                    label={undefined}
                  >
                    <OptionsList
                      onChange={this.handleLeadGroupChange}
                      label="Change business group"
                      value="Business group"
                      options={leadGroup.groupDropdown}
                    />
                  </DropdownContainer>
                </div>
              )}
            </div>
          )}

        {openLeadDetails !== null &&
          openLeadEditingDetails === false &&
          openLeadDetails.npiNumber && (
            <div className={classNames(s.group, s.npiNumber)}>
              <img className={s.icon} src={idIcon} />

              <a href={this.npiUrl(openLeadDetails.npiNumber)} target="_blank">
                <span>NPI: </span>
                <strong>{openLeadDetails.npiNumber}</strong>

                <img src={arrowIcon} alt="" />
              </a>
            </div>
          )}

        {openLeadDetails !== null &&
          (openLeadEditingDetails === false ||
            (openLeadEditingDetails === true &&
              this.props.store.user.userType === UserType.SalesManager)) && (
            <div className={classNames(s.group, s.salesReps)}>
              {openLeadEditingDetails === false && (
                <img className={s.icon} src={repIcon} />
              )}

              {(openLeadEditingDetails === false && (
                <h4>
                  <span>
                    {openLeadDetails.salesReps.length > 1
                      ? 'Reps:'
                      : openLeadDetails.salesReps.length === 1
                      ? 'Rep:'
                      : 'No sales reps assigned'}
                  </span>
                  {openLeadDetails.salesReps.length > 0 && (
                    <strong>{this.commaSeparatedStringOfLeadsSalesReps}</strong>
                  )}
                </h4>
              )) ||
                (openLeadEditingDetails && (
                  <div>
                    <h5>Assigned sales reps:</h5>

                    <DropdownContainer
                      ref={this.referenceSalesRepsDropdown}
                      value={
                        this.salesRepsOptions.length > 0
                          ? undefined
                          : 'No additional reps available'
                      }
                      label={
                        this.salesRepsOptions.length > 0
                          ? 'Add sales rep'
                          : undefined
                      }
                    >
                      <OptionsList
                        label="Sales people"
                        value={null}
                        onChange={this.handleAddingSalesRep}
                        options={
                          this.leadSalesReps.length >= 2
                            ? [
                                {
                                  value: null,
                                  label:
                                    'Only two sales reps are allowed. ' +
                                    'Unassign other reps first.',
                                },
                              ]
                            : this.salesRepsOptions.length > 0
                            ? [
                                {
                                  value: null,
                                  label: 'None',
                                },
                                ...this.salesRepsOptions,
                              ]
                            : [
                                {
                                  value: null,
                                  label: 'No reps available',
                                },
                              ]
                        }
                      />
                    </DropdownContainer>

                    {this.leadSalesReps.map((rep) => (
                      <div key={rep.id} className={s.editRepGroup}>
                        <div className={s.editRepGroupContact}>
                          <p>{rep.name || rep.username}</p>

                          <img src={repIcon} alt="" />
                        </div>

                        <div className={s.editRepGroupActions}>
                          <Checkbox
                            onChange={this.handleChangingSalesRepHierarchy(
                              rep.id
                            )}
                            checked={rep.primary ? true : false}
                            value={true}
                            valueUnchecked={false}
                            label={rep.primary ? 'Main rep' : 'Secondary rep'}
                          />

                          {this.leadSalesReps.length >= 0 && (
                            <IconButton
                              onClick={this.handleRemovingSalesRep(rep.id)}
                              label="Delete"
                              showLabel={false}
                              icon={deleteIcon}
                              labelOnRight={false}
                              removePadding={true}
                            />
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ))}
            </div>
          )}

        {openLeadDetails !== null && (
          <div className={classNames(s.group, s.relationTypeGroup)}>
            {openLeadEditingDetails === false &&
              (this.openLeadProviderType !== null ||
                this.openLeadDispenserClass !== null) && (
                <img className={s.icon} src={businessTypeIcon} />
              )}

            {(openLeadEditingDetails === false &&
              (this.openLeadProviderType !== null ||
                this.openLeadDispenserClass !== null) && (
                <div>
                  {this.openLeadProviderType && (
                    <h4 title="Business type">
                      {this.openLeadProviderType.name}
                    </h4>
                  )}

                  {this.openLeadDispenserClass && (
                    <h4 title="Dispenser class">
                      {this.openLeadDispenserClass.name}
                    </h4>
                  )}
                </div>
              )) ||
              (openLeadEditingDetails && (
                <div>
                  <h5>Business type:</h5>

                  <DropdownContainer
                    ref={this.referenceProviderTypeDropdown}
                    value={editedOpenLeadDetails.providerTypeName || undefined}
                    label={
                      editedOpenLeadDetails.providerTypeName
                        ? undefined
                        : 'Select business type'
                    }
                  >
                    <OptionsList
                      label="Business types"
                      onChange={this.handleProviderTypeChange}
                      value={editedOpenLeadDetails.providerTypeCode}
                      options={this.providerTypesOptions}
                    />
                  </DropdownContainer>

                  <h5>Dispenser class:</h5>

                  <DropdownContainer
                    ref={this.referenceDispenserClassDropdown}
                    value={
                      editedOpenLeadDetails.dispenserClassName || undefined
                    }
                    label={
                      editedOpenLeadDetails.dispenserClassName
                        ? undefined
                        : 'Select dispenser class name'
                    }
                  >
                    <OptionsList
                      label="Dispenser classes"
                      onChange={this.handleDispenserClassChange}
                      value={editedOpenLeadDetails.dispenserClassCode}
                      options={this.dispenserClassesOptions}
                    />
                  </DropdownContainer>
                </div>
              ))}
          </div>
        )}

        {openLeadDetails !== null && this.drugLicenseTypeOptions.length && (
          <div className={classNames(s.group, s.drugLicenseTypeGroup)}>
            {openLeadEditingDetails === false &&
              openLeadDetails.typeOfDrugLicenseData !== null &&
              openLeadDetails.typeOfDrugLicenseData.id !== null && (
                <img className={s.icon} src={licenseTypeIcon} />
              )}

            {(openLeadEditingDetails === false &&
              openLeadDetails.typeOfDrugLicenseData !== null &&
              openLeadDetails.typeOfDrugLicenseData.id !== null && (
                <h4 title="License type">
                  {openLeadDetails.typeOfDrugLicenseData.name}
                </h4>
              )) ||
              (openLeadEditingDetails && (
                <div>
                  <h5>License type:</h5>

                  {drugLicenseTypesLength && (
                    <DropdownContainer
                      ref={this.referenceLicenseTypeDropdown}
                      value={editedOpenLeadDetails.drugLicenseTypeName}
                      label={
                        editedOpenLeadDetails.drugLicenseTypeName
                          ? undefined
                          : 'Select license type'
                      }
                    >
                      <OptionsList
                        label="License types"
                        onChange={this.handleTypeOfLicenseChange}
                        value={editedOpenLeadDetails.drugLicenseTypeId}
                        options={this.drugLicenseTypeOptions}
                      />
                    </DropdownContainer>
                  )}
                </div>
              ))}
          </div>
        )}

        {openLeadDetails !== null &&
          (openLeadAddress || openLeadEditingDetails) && (
            <div className={s.group}>
              <img className={s.icon} src={addressIcon} />
              {(openLeadEditingDetails && (
                <div>
                  <h5>Address:</h5>
                  <label className={s.editSection}>
                    <p>
                      <span>Address 1</span>
                    </p>
                    <input
                      type="text"
                      value={editedOpenLeadDetails.plAddress1}
                      onChange={this.handleOpenLeadDetailsChange(
                        LeadEditableProperties.plAddress1
                      )}
                    />
                  </label>
                  <label className={s.editSection}>
                    <p>
                      <span>Address 2</span>
                    </p>
                    <input
                      type="text"
                      value={editedOpenLeadDetails.plAddress2}
                      onChange={this.handleOpenLeadDetailsChange(
                        LeadEditableProperties.plAddress2
                      )}
                    />
                  </label>
                  <label className={s.editSection}>
                    <p>
                      <span>City</span>
                    </p>
                    <input
                      type="text"
                      value={editedOpenLeadDetails.plCity}
                      onChange={this.handleOpenLeadDetailsChange(
                        LeadEditableProperties.plCity
                      )}
                    />
                  </label>

                  <label className={s.editSection}>
                    <p>
                      <span>State</span>
                    </p>
                    <input
                      type="text"
                      value={editedOpenLeadDetails.plState}
                      onChange={this.handleOpenLeadDetailsChange(
                        LeadEditableProperties.plState
                      )}
                    />
                  </label>

                  <label className={s.editSection}>
                    <p>
                      <span>ZIP code</span>
                    </p>
                    <input
                      type="text"
                      value={editedOpenLeadDetails.plZipcode}
                      onChange={this.handleOpenLeadDetailsChange(
                        LeadEditableProperties.plZipcode
                      )}
                    />
                  </label>
                </div>
              )) ||
                (openLeadAddress && (
                  <ul title="Location">
                    <li>
                      <p>{openLeadAddress}</p>
                    </li>
                  </ul>
                ))}
            </div>
          )}

        {openLeadDetails !== null &&
          (openLeadDetails.plPhone ||
            openLeadDetails.plFax ||
            openLeadEditingDetails) && (
            <div className={s.group}>
              <img className={s.icon} src={contactIcon} />
              {(openLeadEditingDetails && (
                <div>
                  <h5>Contact:</h5>

                  <label className={s.editSection}>
                    <p>
                      <span>Phone number</span>
                    </p>
                    <input
                      type="text"
                      value={editedOpenLeadDetails.plPhone}
                      onChange={this.handleOpenLeadDetailsChange(
                        LeadEditableProperties.plPhone
                      )}
                    />
                  </label>

                  <label className={s.editSection}>
                    <p>
                      <span>Phone number extension</span>
                    </p>
                    <input
                      type="text"
                      value={editedOpenLeadDetails.plExt}
                      onChange={this.handleOpenLeadDetailsChange(
                        LeadEditableProperties.plExt
                      )}
                    />
                  </label>

                  <label className={s.editSection}>
                    <p>
                      <span>Fax number</span>
                    </p>
                    <input
                      type="text"
                      value={editedOpenLeadDetails.plFax}
                      onChange={this.handleOpenLeadDetailsChange(
                        LeadEditableProperties.plFax
                      )}
                    />
                  </label>

                  <label className={s.editSection}>
                    <p>
                      <span>Email address</span>
                    </p>
                    <input
                      type="text"
                      value={editedOpenLeadDetails.plEmail}
                      onChange={this.handleOpenLeadDetailsChange(
                        LeadEditableProperties.plEmail
                      )}
                    />
                  </label>
                </div>
              )) ||
                ((openLeadDetails.plPhone ||
                  openLeadDetails.plExt ||
                  openLeadDetails.plFax ||
                  openLeadDetails.plEmail) && (
                  <ul title="Contact">
                    {openLeadDetails.plPhone && (
                      <li>
                        <p>
                          <a
                            href={`tel:+1 ${openLeadDetails.plPhone.replace(
                              / /g,
                              ''
                            )}`}
                          >
                            {`P. ${openLeadDetails.plPhone}`}
                          </a>
                        </p>
                      </li>
                    )}

                    {openLeadDetails.plExt && (
                      <li>
                        <p>
                          <span>{`Extension: ${openLeadDetails.plExt}`}</span>
                        </p>
                      </li>
                    )}

                    {openLeadDetails.plFax && (
                      <li>
                        <p>
                          <span>{`F: ${openLeadDetails.plFax}`}</span>
                        </p>
                      </li>
                    )}

                    {openLeadDetails.plEmail && (
                      <li>
                        <p>
                          <a
                            title={openLeadDetails.plEmail}
                            href={`mailto:${openLeadDetails.plEmail}`}
                          >
                            {`E. ${openLeadDetails.plEmail}`}
                          </a>
                        </p>
                      </li>
                    )}
                  </ul>
                ))}
            </div>
          )}

        {openLeadEditingDetails && (
          <div className={s.details}>
            <div>
              <button className="boxButton" onClick={this.handleSaveChanges}>
                Save changes
              </button>
            </div>
            <div>
              <SavingIndicator
                labels={{
                  saving: 'Saving changes...',
                  error: 'Could not save changes, try again',
                }}
                status={
                  openLeadEditingDetailsSavingError
                    ? SavingIndicatorStatus.Error
                    : openLeadEditingDetailsSaving
                    ? SavingIndicatorStatus.Saving
                    : SavingIndicatorStatus.None
                }
              />
            </div>
          </div>
        )}
      </div>
    ) : null;
  }

  private leadGroupDropdown: DropdownContainer = null;
  private referenceLeadGroupDropdown = (ref: DropdownContainer) =>
    (this.leadGroupDropdown = ref);

  private salesRepsDropdown: DropdownContainer = null;
  private referenceSalesRepsDropdown = (ref: DropdownContainer) =>
    (this.salesRepsDropdown = ref);

  private providerTypeDropdown: DropdownContainer = null;
  private referenceProviderTypeDropdown = (ref: DropdownContainer) =>
    (this.providerTypeDropdown = ref);

  private dispenserClassDropdown: DropdownContainer = null;
  private referenceDispenserClassDropdown = (ref: DropdownContainer) =>
    (this.dispenserClassDropdown = ref);

  private licenseTypeDropdown: DropdownContainer = null;
  private referenceLicenseTypeDropdown = (ref: DropdownContainer) =>
    (this.licenseTypeDropdown = ref);

  @action private loadLeadGroups = () => {
    const { leadGroup, lead } = this.props.store;

    if (leadGroup.showList) {
      this.props.store.leadGroup.showList = false;
    } else {
      this.props.store.leadGroup.showList = true;
      leadGroup.fetchLeadGroupDetails(lead.openLeadDetails.providerGroup.id);
    }
  };

  @action private handleAddingSalesRep = (selection: {
    value: number | null;
    label: string;
  }) => {
    if (selection.value) {
      const filteredReps = this.props.store.users.salesReps.filter(
        (rep) => rep.id === selection.value
      );

      if (filteredReps.length > 0) {
        const rep = filteredReps[0];
        const otherRepsCount = this.props.store.lead.editedOpenLeadDetails
          .salesReps.length;
        this.props.store.lead.editedOpenLeadDetails.salesReps = [
          {
            id: rep.id,
            email: rep.email,
            username: rep.username,
            name: rep.name,
            slpcode: rep.slpcode,
            primary: otherRepsCount <= 0 ? 1 : 0,
            status: true,
          },
          ...this.props.store.lead.editedOpenLeadDetails.salesReps.slice(),
        ];

        this.props.store.lead.editedOpenLeadDetails.modified = true;
      }
    }

    if (this.salesRepsDropdown) {
      this.salesRepsDropdown.closeDropdown();
    }
  };

  @action private handleChangingSalesRepHierarchy = (salesRepId: number) => (
    checked: boolean,
    value: boolean
  ) => {
    this.props.store.lead.editedOpenLeadDetails.salesReps = this.props.store.lead.editedOpenLeadDetails.salesReps
      .slice()
      .map((rep) => {
        if (rep.id === salesRepId) {
          return {
            ...rep,
            primary: checked ? (1 as 1 | 0) : (0 as 1 | 0),
          };
        } else {
          return {
            ...rep,
            primary: checked ? (0 as 1 | 0) : (rep.primary as 1 | 0),
          };
        }
      });

    this.props.store.lead.editedOpenLeadDetails.modified = true;
  };

  @action private handleRemovingSalesRep = (salesRepId: number) => (
    e: React.MouseEvent<HTMLButtonElement>
  ) => {
    let salesRepIndex: number = -1;
    this.props.store.lead.editedOpenLeadDetails.salesReps.some(
      (rep, repIndex) => {
        if (rep.id === salesRepId) {
          salesRepIndex = repIndex;
          return true;
        }

        return false;
      }
    );

    if (salesRepIndex !== -1) {
      this.props.store.lead.editedOpenLeadDetails.salesReps = [
        ...this.props.store.lead.editedOpenLeadDetails.salesReps.slice(
          0,
          salesRepIndex
        ),
        ...this.props.store.lead.editedOpenLeadDetails.salesReps.slice(
          salesRepIndex + 1
        ),
      ];

      // If primary rep has been removed, turn first rep into primary rep
      const isAnyRepPrimary = this.props.store.lead.editedOpenLeadDetails.salesReps.some(
        (rep) => (rep.primary === 1 ? true : false)
      );
      if (
        isAnyRepPrimary === false &&
        this.props.store.lead.editedOpenLeadDetails.salesReps.length > 0
      ) {
        this.props.store.lead.editedOpenLeadDetails.salesReps[0].primary = 1;
      }

      this.props.store.lead.editedOpenLeadDetails.modified = true;
    }
  };

  @action private handleLeadGroupChange = (changed: {
    value: string;
    label: string;
  }) => {
    if (changed.value === 'assign') {
      this.props.store.leadGroup.modalStatus =
        LeadGroupModalStatus.OpenedOnGroupList;
      this.props.store.leadGroup.fetchLeadGroups();
    } else if (changed.value === 'remove') {
      this.props.store.leadGroup.addLeadToGroup(null);
    } else if (changed.value === 'name') {
      // ??
    }

    if (this.leadGroupDropdown) {
      this.leadGroupDropdown.closeDropdown();
    }
  };

  @action private handleProviderTypeChange = (changed: {
    value: string;
    label: string;
  }) => {
    const {
      lead: { editedOpenLeadDetails },
    } = this.props.store;
    editedOpenLeadDetails.providerTypeCode = changed.value;
    editedOpenLeadDetails.providerTypeName = changed.label;
    editedOpenLeadDetails.modified = true;

    if (this.providerTypeDropdown) {
      this.providerTypeDropdown.closeDropdown();
    }
  };

  @action private handleDispenserClassChange = (changed: {
    value: string;
    label: string;
  }) => {
    const {
      lead: { editedOpenLeadDetails },
    } = this.props.store;
    editedOpenLeadDetails.dispenserClassCode = changed.value;
    editedOpenLeadDetails.dispenserClassName = changed.label;
    editedOpenLeadDetails.modified = true;

    if (this.dispenserClassDropdown) {
      this.dispenserClassDropdown.closeDropdown();
    }
  };

  @action private handleTypeOfLicenseChange = (changed: {
    value: number;
    label: string;
  }) => {
    this.props.store.lead.editedOpenLeadDetails.drugLicenseTypeId =
      changed.value;
    this.props.store.lead.editedOpenLeadDetails.drugLicenseTypeName =
      changed.label;
    this.props.store.lead.editedOpenLeadDetails.modified = true;

    if (this.licenseTypeDropdown) {
      this.licenseTypeDropdown.closeDropdown();
    }
  };

  @action private handleOpenLeadDetailsChange = (
    property: LeadEditableProperties
  ) => (e: React.FormEvent<HTMLInputElement>) => {
    this.props.store.lead.editedOpenLeadDetails[property] =
      e.currentTarget.value;
    this.props.store.lead.editedOpenLeadDetails.modified = true;
  };

  private handleSaveChanges = async () => {
    await this.props.store.lead.saveUpdatedLeadDetails();
    scrollWindow.toY(0);
  };

  private npiUrl = (npi: string): string => {
    return `http://www.hipaaspace.com/Medical_Billing/Coding/NPI/Codes/NPI_${npi}.aspx`;
  };
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--0!./app/web/src/routes/sales/Lead/Details/LeadDetailsMaster.tsx